import {
  Clipboard,
  Download,
  FileCheck2,
  FileText,
  FileUp,
  Sparkles,
  Target,
  Trash2,
} from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  analyzeResume,
  compareLatestResumeReports,
  deleteResumeReport,
  generateResumeProjectSection,
  getStudentGrowthMe,
  getMyGithubRepositories,
  getResumeReport,
  getResumeReports,
  isFeatureLimitExceeded,
  optimizeResumeSection,
  recordStudentGrowthEvent,
  recordResumeDraftUsage,
  recordResumeExportUsage,
} from '../api.js'
import { useAuth } from '../auth-context.js'
import FeatureLimitCard from '../components/FeatureLimitCard.jsx'
import MembershipStatusCard from '../components/MembershipStatusCard.jsx'
import StudentBackButton from '../components/StudentBackButton.jsx'
import { useLanguage } from '../contexts/LanguageContext.jsx'
import {
  copyResumeAnalysisText,
  downloadResumeAnalysisDocx,
  downloadResumeAnalysisPdf,
} from '../resume-export.js'
import {
  STUDENT_GRADES,
  STUDENT_MAJOR_GROUPS,
  STUDENT_PROFILE_GOALS,
  buildInitialResumeDraft,
  getStudentOptionLabel,
  readStoredStudentProfile,
  saveStoredStudentProfile,
} from '../student-projects.js'

const MAX_FILE_BYTES = 10 * 1024 * 1024

const text = {
  zh: {
    heroEyebrow: '简历资产',
    heroTitle: '优化简历，准备投递',
    heroSubtitle: '上传简历，AI 帮你检查结构、项目经历、表达方式和岗位匹配度，并生成可直接修改的建议',
    existing: '上传简历分析',
    new: '没有简历，先生成初稿',
    existingDesc: '支持 PDF / DOCX，分析后自动保存历史记录',
    newDesc: '根据专业、年级和目标生成参考初稿，不虚构经历',
    uploadTitle: '上传 PDF / DOCX 简历',
    uploadHint: '文件大小 10MB 内。第一版不长期保存原始简历文件，只保存分析结果',
    chooseFile: '选择文件',
    analyze: '开始分析',
    analyzing: '正在分析...',
    fileIdle: '等待上传',
    fileReady: '已选择，等待分析',
    fileDone: '分析完成',
    targetRole: '目标岗位',
    targetGoal: '目标方向',
    githubUrl: 'GitHub 项目地址（可选）',
    jdTitle: '岗位定向优化',
    jdPlaceholder: '粘贴岗位 JD，AI 会提取关键词、缺失能力和修改方向',
    jdButton: '开始岗位匹配优化',
    jdJump: '跳转岗位定向求职助手',
    githubTitle: '关联我的 GitHub 项目',
    githubDesc: '选择你分析过GitHub 项目，AI 可以帮你生成更适合写进简历的项目经历',
    generateProject: '生成项目经历描述',
    copy: '复制',
    copied: '已复制',
    markDone: '标记已修',
    applyDraft: '应用到草稿',
    reason: '展开原因',
    profileLabel: '当前画像',
    editProfile: '修改画像',
    majorDirection: '专业方向',
    grade: '年级',
    currentGoal: '当前目标',
    currentGrowthGoal: '当前成长目标',
    currentGrowthGoalDesc: '本次简历分析会优先围绕这个目标检查技能、项目经历和表达匹配度',
    backGrowth: '返回成长中心',
    saveAndDraft: '保存并生成初稿',
    generateDraft: '生成我的初版简历',
    draftResultTitle: '初版简历参考',
    analysisResult: '分析结果',
    analysisFallback: 'AI 已完成分析，请查看评分和建议',
    totalReview: '简历总评',
    targetMatch: '当前目标匹配',
    scoreBreakdown: '分项评分',
    scoreStructure: '结构评分',
    scoreProject: '项目经历评分',
    scoreExpression: '表达评分',
    scoreJobMatch: '岗位匹配评分',
    strengths: '优点',
    skillGaps: '能力差距',
    projectAdvice: '项目经历建议',
    mainIssues: '主要问题',
    noIssues: '暂无明显问题',
    prioritySuggestions: '优先修改建议',
    suggestion: '建议',
    originalText: '原文',
    noSuggestions: '暂无建议',
    copyReady: '可直接复制的优化文案',
    projectExperience: '项目经历',
    skillKeywords: '技能关键词',
    nextSteps: '下一步',
    draftAdded: '已加入草稿：{{count}} 条，可在后续 DOCX 生成中使用',
    historyEyebrow: '历史记录',
    refresh: '刷新',
    compareImproved: '本次比上次提升 {{delta}}',
    compareDropped: '本次比上次下降 {{delta}}',
    compareBetter: '变好',
    compareRemaining: '仍需处理',
    compareNext: '下一步',
    compareHint: '再分析一次后，就能对比本次和上次是否有提升',
    fileFallback: '简历',
    historyProjectScore: '项目',
    historyExpressionScore: '表达',
    viewDetail: '查看详情',
    delete: '删除',
    githubEyebrow: '项目材料',
    selectProject: '选择项目',
    generating: '生成中...',
    projectSectionFallback: '简历项目标题',
    techStack: '技术栈',
    interviewFocus: '面试表达重点',
    jdEyebrow: '岗位定向',
    optimizing: '优化中...',
    jdKeywords: 'JD 关键词',
    missingAbilities: '缺失能力',
    rewriteDirections: '推荐修改方向',
    projectAlignment: '项目经历贴合方式',
    interviewQuestions: '面试可能会问',
    history: '历史分析记录',
    emptyHistory: '上传第一份简历，AI 会帮你找出问题并保存历史记录',
    exportNote: '导出额度：免费用户每1 次导出，VIP 用户更多导出次数。DOCX 简历生成后续再做',
    pdfExport: '导出优化建议 PDF',
    checklistExport: '导出简历修改清',
    docxExport: '导出当前建议 DOCX',
    draftWarning: '以下内容仅作参考，请替换为真实经历，不要编造项目、实习或奖项',
  },
  en: {
    heroEyebrow: 'Resume assets',
    heroTitle: 'Optimize Your Resume Before Applying',
    heroSubtitle: 'Upload a resume and let AI check structure, project experience, wording, job match, and copy-ready edits.',
    existing: 'Upload resume',
    new: 'Create first draft',
    existingDesc: 'PDF / DOCX uploads with saved analysis history.',
    newDesc: 'Generate a reference draft from your profile without fabricating experience.',
    uploadTitle: 'Upload PDF / DOCX resume',
    uploadHint: 'Up to 10MB. The first version stores analysis results only, not the original file.',
    chooseFile: 'Choose file',
    analyze: 'Analyze resume',
    analyzing: 'Analyzing...',
    fileIdle: 'Waiting for upload',
    fileReady: 'Ready to analyze',
    fileDone: 'Analysis complete',
    targetRole: 'Target role',
    targetGoal: 'Target goal',
    githubUrl: 'GitHub project URL (optional)',
    jdTitle: 'JD-targeted optimization',
    jdPlaceholder: 'Paste a job description to extract keywords, missing skills, and rewrite directions.',
    jdButton: 'Start JD match',
    jdJump: 'Open job assistant',
    githubTitle: 'Connect My GitHub Projects',
    githubDesc: 'Pick an analyzed GitHub project and generate resume-ready project bullets.',
    generateProject: 'Generate project bullets',
    copy: 'Copy',
    copied: 'Copied',
    markDone: 'Mark updated',
    applyDraft: 'Apply to draft',
    reason: 'Show reason',
    history: 'Analysis history',
    emptyHistory: 'Upload your first resume and AI will save the analysis history here.',
    exportNote: 'Export quota: free users get 1 export per month. VIP users get more export quota.',
    pdfExport: 'Export suggestions PDF',
    checklistExport: 'Export edit checklist',
    docxExport: 'Export current suggestions DOCX',
    draftWarning: 'Reference only. Replace examples with real experience and do not fabricate projects, internships, or awards.',
    profileLabel: 'Current profile',
    editProfile: 'Edit profile',
    majorDirection: 'Major direction',
    grade: 'Grade',
    currentGoal: 'Current goal',
    currentGrowthGoal: 'Current growth goal',
    currentGrowthGoalDesc: 'This analysis will prioritize skills, project experience, and wording that match this goal.',
    backGrowth: 'Back to My Growth',
    saveAndDraft: 'Save and create draft',
    generateDraft: 'Generate my first resume draft',
    draftResultTitle: 'First draft reference',
    analysisResult: 'Analysis result',
    analysisFallback: 'Analysis is ready. Review scores and suggestions.',
    totalReview: 'Resume review',
    targetMatch: 'Target match',
    scoreBreakdown: 'Score breakdown',
    scoreStructure: 'Structure score',
    scoreProject: 'Project experience score',
    scoreExpression: 'Wording score',
    scoreJobMatch: 'Job match score',
    strengths: 'Strengths',
    skillGaps: 'Skill gaps',
    projectAdvice: 'Project experience advice',
    mainIssues: 'Main issues',
    noIssues: 'No obvious issues yet',
    prioritySuggestions: 'Priority edits',
    suggestion: 'Suggestion',
    originalText: 'Original',
    noSuggestions: 'No suggestions yet',
    copyReady: 'Copy-ready wording',
    projectExperience: 'Project experience',
    skillKeywords: 'Skill keywords',
    nextSteps: 'Next steps',
    draftAdded: '{{count}} items added to draft for later DOCX generation',
    historyEyebrow: 'History',
    refresh: 'Refresh',
    compareImproved: 'Improved by {{delta}} from last time',
    compareDropped: 'Dropped by {{delta}} from last time',
    compareBetter: 'Improved',
    compareRemaining: 'Still needs work',
    compareNext: 'Next',
    compareHint: 'Analyze one more resume to compare this result with the previous one.',
    fileFallback: 'Resume',
    historyProjectScore: 'Project',
    historyExpressionScore: 'Expression',
    viewDetail: 'View detail',
    delete: 'Delete',
    githubEyebrow: 'Project materials',
    selectProject: 'Select project',
    generating: 'Generating...',
    projectSectionFallback: 'Resume project title',
    techStack: 'Tech stack',
    interviewFocus: 'Interview focus',
    jdEyebrow: 'Job targeting',
    optimizing: 'Optimizing...',
    jdKeywords: 'JD keywords',
    missingAbilities: 'Missing abilities',
    rewriteDirections: 'Recommended rewrite directions',
    projectAlignment: 'Project alignment',
    interviewQuestions: 'Likely interview questions',
  },
}

function safeList(value) {
  return Array.isArray(value) ? value.filter(Boolean) : []
}

function safeText(value, fallback = '') {
  return typeof value === 'string' && value.trim() ? value.trim() : fallback
}

function clampScore(value) {
  const number = Number(value)
  return Number.isFinite(number) ? Math.max(0, Math.min(100, Math.round(number))) : null
}

function normalizeAnalysis(value) {
  const source = value && typeof value === 'object' && !Array.isArray(value) ? value : {}
  const scores = source.scores && typeof source.scores === 'object' ? source.scores : {}
  return {
    score: clampScore(source.score),
    scores: {
      structure: clampScore(scores.structure),
      project: clampScore(scores.project),
      expression: clampScore(scores.expression),
      jobMatch: clampScore(scores.jobMatch),
    },
    summary: safeText(source.summary),
    strengths: safeList(source.strengths),
    issues: safeList(source.issues),
    suggestions: safeList(source.suggestions),
    targetMatch: source.targetMatch && typeof source.targetMatch === 'object' ? source.targetMatch : null,
    skillGaps: safeList(source.skillGaps || source.missingSkills || source.missingAbilities),
    projectAdvice: safeList(source.projectAdvice || source.projectExperienceAdvice || source.projectSuggestions),
    copyReadyText: safeList(source.copyReadyText || source.copywriting || source.rewriteText),
    optimizedSections: source.optimizedSections && typeof source.optimizedSections === 'object'
      ? source.optimizedSections
      : {},
    nextSteps: safeList(source.nextSteps),
  }
}

function formatFileSize(bytes) {
  if (!Number.isFinite(bytes)) return ''
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

function isSupportedFile(file) {
  const name = String(file?.name || '').toLowerCase()
  const type = String(file?.type || '').toLowerCase()
  return name.endsWith('.pdf')
    || name.endsWith('.docx')
    || type === 'application/pdf'
    || type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
}

function formatTime(value) {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return String(value || '').slice(0, 16)
  return new Intl.DateTimeFormat('zh-CN', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date)
}

function reportScore(report, key = 'resumeScore') {
  const value = Number(report?.[key])
  return Number.isFinite(value) ? Math.round(value) : '--'
}

function buildCopyReadyText(analysis, targetName) {
  const explicit = safeList(analysis.copyReadyText).map((item) => String(item))
  const projectBullets = safeList(analysis.optimizedSections.projects)
    .flatMap((project) => safeList(project.bullets))
    .map((item) => String(item))
  const suggestionText = safeList(analysis.suggestions)
    .map((item) => item?.after || item?.optimized || item?.text)
    .filter(Boolean)
    .map((item) => String(item))
  const profileText = safeText(analysis.optimizedSections.profile)
  const merged = [
    profileText,
    ...explicit,
    ...projectBullets,
    ...suggestionText,
  ].filter(Boolean)
  if (merged.length) return [...new Set(merged)].slice(0, 6)
  if (!targetName) return []
  return [
    `求职方向：${targetName}。建议在简历开头突出与目标岗位直接相关的技能、项目和结果。`,
    '项目经历建议使用“目标 - 行动 - 技术方法 - 结果”的结构，避免只写负责或参与。',
  ]
}

function buildSkillGaps(analysis, targetName) {
  const gaps = safeList(analysis.skillGaps)
  if (gaps.length) return gaps
  if (!targetName) return []
  return [`补齐 ${targetName} 相关核心技能关键词`, '补充能证明能力的项目结果或作品链']
}

function buildProjectAdvice(analysis) {
  const advice = safeList(analysis.projectAdvice)
  if (advice.length) return advice
  return safeList(analysis.suggestions)
    .map((item) => item?.title || item?.reason || item?.text)
    .filter(Boolean)
    .slice(0, 3)
}

function quotaText(user, language) {
  const membership = user?.membership || {}
  const item = user?.usageSummary?.resume_analyze || {}
  const isVip = Boolean(membership.isVip || membership.isAdmin || membership.tier === 'vip' || membership.tier === 'admin')
  if (item.limit === null) return language === 'en' ? 'Unlimited quota' : '不限额度'
  const limit = Number.isFinite(Number(item.limit)) ? Number(item.limit) : (isVip ? 30 : 3)
  const remaining = item.remaining ?? Math.max(0, limit - Number(item.used || 0))
  if (language === 'en') return isVip ? `Today remaining ${remaining} / ${limit}` : `This month remaining ${remaining} / ${limit}`
  return isVip ? `今日剩余 ${remaining} / ${limit}` : `本月剩余 ${remaining} / ${limit}`
}

function ResumeOptionGroup({ active, language, onSelect, options }) {
  return (
    <div className="resume-profile-options">
      {options.map((item) => (
        <button
          aria-pressed={active === item.key}
          className={active === item.key ? 'is-active' : ''}
          key={item.key}
          type="button"
          onClick={() => onSelect(item.key)}
        >
          {item[language === 'en' ? 'en' : 'zh']}
        </button>
      ))}
    </div>
  )
}

function ScoreTile({ label, value }) {
  return (
    <div>
      <span>{label}</span>
      <strong>{value === null || value === undefined ? '--' : value}</strong>
    </div>
  )
}

function ListBlock({ items, title }) {
  const list = safeList(items)
  return (
    <article className="resume-result-card">
      <h2><FileCheck2 size={18} />{title}</h2>
      {list.length ? <ul>{list.map((item, index) => <li key={`${title}-${index}`}>{String(item)}</li>)}</ul> : <p>暂无内容</p>}
    </article>
  )
}

function StudentResumePage() {
  const { user, refreshUser } = useAuth() || {}
  const { language } = useLanguage()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const t = text[language] || text.zh
  const fileInputRef = useRef(null)
  const [mode, setMode] = useState('existing')
  const [file, setFile] = useState(null)
  const [status, setStatus] = useState('idle')
  const [targetRole, setTargetRole] = useState('')
  const [targetGoal, setTargetGoal] = useState('')
  const [growthTarget, setGrowthTarget] = useState('')
  const [growthPlanId, setGrowthPlanId] = useState('')
  const [githubUrl, setGithubUrl] = useState('')
  const [error, setError] = useState('')
  const [limitError, setLimitError] = useState(null)
  const [result, setResult] = useState(null)
  const [reports, setReports] = useState([])
  const [comparison, setComparison] = useState(null)
  const [historyError, setHistoryError] = useState('')
  const [selectedReport, setSelectedReport] = useState(null)
  const [copied, setCopied] = useState('')
  const [doneSuggestions, setDoneSuggestions] = useState(() => new Set())
  const [draftSections, setDraftSections] = useState([])
  const [studentProfile, setStudentProfile] = useState(() => readStoredStudentProfile())
  const [profileDraft, setProfileDraft] = useState(() => readStoredStudentProfile() || {
    majorGroup: 'general',
    grade: 'junior',
    goal: 'internship',
    targetRole: '',
  })
  const [showProfileForm, setShowProfileForm] = useState(false)
  const [draftResume, setDraftResume] = useState(null)
  const [githubProjects, setGithubProjects] = useState([])
  const [selectedGithubProject, setSelectedGithubProject] = useState('')
  const [projectSection, setProjectSection] = useState(null)
  const [projectLoading, setProjectLoading] = useState(false)
  const [jdText, setJdText] = useState('')
  const [jdResult, setJdResult] = useState(null)
  const [jdLoading, setJdLoading] = useState(false)
  const resumeEntryRecordedRef = useRef(false)

  const analysis = useMemo(() => normalizeAnalysis(result?.analysis || selectedReport?.rawResult), [result, selectedReport])
  const activeTargetName = safeText(targetRole, safeText(growthTarget, targetGoal))
  const targetMatchScore = analysis.targetMatch?.score ?? analysis.scores.jobMatch
  const targetMatchSummary = analysis.targetMatch?.summary || (activeTargetName ? `Analysis focuses on ${activeTargetName}.` : 'Fill in a target role to get clearer match advice.')
  const targetSkillGaps = buildSkillGaps(analysis, activeTargetName)
  const targetProjectAdvice = buildProjectAdvice(analysis)
  const copyReadyText = buildCopyReadyText(analysis, activeTargetName)
  const statusText = status === 'completed' ? t.fileDone : status === 'ready' ? t.fileReady : t.fileIdle

  const loadHistory = useCallback(async (signal) => {
    if (!user) {
      setReports([])
      setComparison(null)
      return
    }
    try {
      const [reportPayload, comparePayload] = await Promise.all([
        getResumeReports(signal),
        compareLatestResumeReports(signal),
      ])
      setReports(Array.isArray(reportPayload?.items) ? reportPayload.items : [])
      setComparison(comparePayload?.comparison || null)
    } catch (err) {
      if (err.name !== 'AbortError') setHistoryError(err.message || '历史记录加载失败')
    }
  }, [user])

  useEffect(() => {
    if (user && typeof refreshUser === 'function') refreshUser().catch(() => {})
  }, [refreshUser, user])

  useEffect(() => {
    const target = searchParams.get('target') || searchParams.get('direction') || ''
    if (target) {
      setGrowthTarget(target)
      setTargetRole((current) => current || target)
      setTargetGoal((current) => current || `围绕 ${target} 优化简历`)
    }
  }, [searchParams])

  useEffect(() => {
    if (!user) return undefined
    const controller = new AbortController()
    getStudentGrowthMe(controller.signal)
      .then((payload) => {
        const plan = payload?.plan || null
        const target = plan?.targetDirection || ''
        setGrowthPlanId(plan?.id || '')
        if (!target) return
        setGrowthTarget((current) => current || target)
        setTargetRole((current) => current || target)
        setTargetGoal((current) => current || `围绕 ${target} 优化简历`)
      })
      .catch(() => {})
    return () => controller.abort()
  }, [user])

  useEffect(() => {
    if (!user || resumeEntryRecordedRef.current) return
    resumeEntryRecordedRef.current = true
    recordStudentGrowthEvent({
      planId: growthPlanId || undefined,
      eventType: 'resume_optimization_entered',
      title: `进入简历优化：${activeTargetName || '当前目标'}`,
      description: '开始围绕成长目标检查简历状态',
      metadata: { target: activeTargetName || growthTarget },
    }).catch(() => {})
  }, [activeTargetName, growthPlanId, growthTarget, user])

  useEffect(() => {
    const controller = new AbortController()
    loadHistory(controller.signal)
    return () => controller.abort()
  }, [loadHistory])

  useEffect(() => {
    if (!user) return undefined
    const controller = new AbortController()
    getMyGithubRepositories(controller.signal)
      .then((payload) => setGithubProjects(Array.isArray(payload?.items) ? payload.items : []))
      .catch(() => setGithubProjects([]))
    return () => controller.abort()
  }, [user])

  const selectFile = (nextFile) => {
    setError('')
    setLimitError(null)
    setResult(null)
    if (!nextFile) {
      setFile(null)
      setStatus('idle')
      return
    }
    if (!isSupportedFile(nextFile)) {
      setError('仅支PDF DOCX 文件')
      setStatus('failed')
      return
    }
    if (nextFile.size > MAX_FILE_BYTES) {
      setError('文件过大，请上传 10MB 内文')
      setStatus('failed')
      return
    }
    setFile(nextFile)
    setStatus('ready')
  }

  const runAnalysis = async () => {
    if (!file) {
      setError('请先上传 PDF DOCX 简')
      return
    }
    setError('')
    setLimitError(null)
    setSelectedReport(null)
    setStatus('analyzing')
    try {
      const payload = await analyzeResume(file, {
        targetRole,
        targetGoal: targetGoal || growthTarget,
        growthTarget,
        githubUrl,
        locale: language === 'en' ? 'en' : 'zh',
      })
      setResult(payload)
      setStatus('completed')
      await loadHistory()
      if (typeof refreshUser === 'function') refreshUser().catch(() => {})
    } catch (err) {
      setStatus('failed')
      if (isFeatureLimitExceeded(err)) setLimitError(err)
      else setError(err.message || 'AI 分析失败，请稍后重试')
    }
  }

  const copyText = async (value) => {
    try {
      await navigator.clipboard.writeText(String(value || ''))
      setCopied(String(value || ''))
      window.setTimeout(() => setCopied(''), 1400)
    } catch {
      setCopied('')
    }
  }

  const recordExportQuota = async () => {
    try {
      await recordResumeExportUsage()
      return true
    } catch (err) {
      if (isFeatureLimitExceeded(err)) setLimitError(err)
      else setError(err.message || '导出失败')
      return false
    }
  }

  const exportPdf = async () => {
    if (await recordExportQuota()) downloadResumeAnalysisPdf(result?.analysis || selectedReport?.rawResult || {}, { textLength: result?.textLength || 0 })
  }

  const exportDocx = async () => {
    if (await recordExportQuota()) downloadResumeAnalysisDocx(result?.analysis || selectedReport?.rawResult || {}, { textLength: result?.textLength || 0 })
  }

  const exportChecklist = async () => {
    if (await recordExportQuota()) copyResumeAnalysisText(result?.analysis || selectedReport?.rawResult || {}, { textLength: result?.textLength || 0 })
  }

  const saveResumeProfile = async () => {
    setLimitError(null)
    try {
      await recordResumeDraftUsage()
      const next = {
        ...profileDraft,
        majorGroup: profileDraft.majorGroup || 'general',
        grade: profileDraft.grade || 'junior',
        goal: profileDraft.goal || 'internship',
        updatedAt: new Date().toISOString(),
      }
      setStudentProfile(next)
      saveStoredStudentProfile(next)
      setShowProfileForm(false)
      setDraftResume(buildInitialResumeDraft(next, language))
    } catch (err) {
      if (isFeatureLimitExceeded(err)) setLimitError(err)
      else setError(err.message || '初稿生成失败')
    }
  }

  const generateInitialResume = async () => {
    setMode('new')
    const current = studentProfile || readStoredStudentProfile()
    if (!current) {
      setShowProfileForm(true)
      setDraftResume(null)
      return
    }
    try {
      await recordResumeDraftUsage()
      setDraftResume(buildInitialResumeDraft(current, language))
    } catch (err) {
      if (isFeatureLimitExceeded(err)) setLimitError(err)
      else setError(err.message || '初稿生成失败')
    }
  }

  const openReport = async (id) => {
    try {
      const payload = await getResumeReport(id)
      setSelectedReport(payload?.report || null)
      setResult(null)
    } catch (err) {
      setHistoryError(err.message || '历史详情加载失败')
    }
  }

  const removeReport = async (id) => {
    try {
      await deleteResumeReport(id)
      if (String(selectedReport?.id || '') === String(id)) setSelectedReport(null)
      await loadHistory()
    } catch (err) {
      setHistoryError(err.message || '删除失败')
    }
  }

  const generateProjectSection = async () => {
    const project = githubProjects.find((item) => String(item.id || item.fullName || item.full_name) === selectedGithubProject)
    if (!project) return
    setProjectLoading(true)
    try {
      const payload = await generateResumeProjectSection({
        project,
        targetRole: activeTargetName,
        growthTarget,
        locale: language === 'en' ? 'en' : 'zh',
      })
      setProjectSection(payload?.section || payload)
    } catch (err) {
      setError(err.message || '项目经历生成失败')
    } finally {
      setProjectLoading(false)
    }
  }

  const optimizeJd = async () => {
    if (!jdText.trim()) return
    setJdLoading(true)
    try {
      const payload = await optimizeResumeSection({
        type: 'job_description',
        jdText,
        resumeAnalysis: result?.analysis || selectedReport?.rawResult || null,
        targetRole: activeTargetName,
        growthTarget,
        locale: language === 'en' ? 'en' : 'zh',
      })
      setJdResult(payload?.result || payload)
    } catch (err) {
      setError(err.message || '岗位匹配优化失败')
    } finally {
      setJdLoading(false)
    }
  }

  const applySuggestion = (value) => {
    setDraftSections((current) => [...current, value].filter(Boolean))
  }

  return (
    <div className="student-page resume-analyzer-page page-container">
      <StudentBackButton />
      <section className="student-hero resume-analyzer-hero">
        <span>{t.heroEyebrow}</span>
        <h1>{t.heroTitle}</h1>
        <p>{t.heroSubtitle}</p>
      </section>

      <section className="resume-entry-tabs" aria-label="resume modes">
        <button className={mode === 'existing' ? 'is-active' : ''} type="button" onClick={() => setMode('existing')}>
          <FileUp size={20} />
          <strong>{t.existing}</strong>
          <span>{t.existingDesc}</span>
        </button>
        <button className={mode === 'new' ? 'is-active' : ''} type="button" onClick={generateInitialResume}>
          <Sparkles size={20} />
          <strong>{t.new}</strong>
          <span>{t.newDesc}</span>
        </button>
      </section>

      <MembershipStatusCard className="resume-membership-card" />
      {activeTargetName && (
        <section className="resume-growth-target-card">
          <div>
            <span>{t.currentGrowthGoal}</span>
            <strong>{activeTargetName}</strong>
            <p>{t.currentGrowthGoalDesc}</p>
          </div>
          <button type="button" className="secondary-button" onClick={() => navigate('/growth')}>
            {t.backGrowth}
          </button>
        </section>
      )}
      <p className="resume-mode-tip">{quotaText(user, language)}</p>

      {mode === 'existing' && (
        <section className="student-form-panel resume-analyzer-panel">
          <div className={`student-upload-box resume-upload-box is-${status}`}>
            <FileUp size={30} />
            <strong>{t.uploadTitle}</strong>
            <p>{t.uploadHint}</p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              onChange={(event) => selectFile(event.target.files?.[0] || null)}
            />
            <button type="button" className="secondary-button" onClick={() => fileInputRef.current?.click()}>
              <FileText size={16} />
              {t.chooseFile}
            </button>
            <div className="resume-file-status">
              <span>{status === 'analyzing' ? t.analyzing : statusText}</span>
              {file ? <strong>{file.name} - {formatFileSize(file.size)}</strong> : <small>PDF / DOCX - 10MB</small>}
            </div>
          </div>

          <div className="resume-extra-fields">
            <label><span>{t.targetRole}</span><input value={targetRole} onChange={(event) => setTargetRole(event.target.value)} placeholder="Java backend intern / product assistant / operations" /></label>
            <label><span>{t.targetGoal}</span><input value={targetGoal} onChange={(event) => setTargetGoal(event.target.value)} placeholder="Find internship / spring recruiting / improve project experience" /></label>
            <label><span>{t.githubUrl}</span><input value={githubUrl} onChange={(event) => setGithubUrl(event.target.value)} placeholder="https://github.com/yourname/project" /></label>
          </div>

          <button className="primary-button resume-submit-button" type="button" disabled={status === 'analyzing'} onClick={runAnalysis}>
            <Sparkles size={17} />
            {status === 'analyzing' ? t.analyzing : t.analyze}
          </button>
          {error && <p className="student-form-error">{error}</p>}
          {limitError && <FeatureLimitCard message={limitError.message} />}
        </section>
      )}

      {mode === 'new' && (
        <section className="resume-draft-panel">
          {studentProfile && !showProfileForm && (
            <div className="resume-profile-summary">
              <span>
                {t.profileLabel}: <strong>{getStudentOptionLabel(STUDENT_MAJOR_GROUPS, studentProfile.majorGroup, language)} / {getStudentOptionLabel(STUDENT_GRADES, studentProfile.grade, language)} / {getStudentOptionLabel(STUDENT_PROFILE_GOALS, studentProfile.goal, language)}</strong>
              </span>
              <button type="button" className="secondary-button" onClick={() => setShowProfileForm(true)}>{t.editProfile}</button>
            </div>
          )}
          {showProfileForm && (
            <div className="resume-profile-form">
              <label><strong>{t.majorDirection}</strong><ResumeOptionGroup active={profileDraft.majorGroup} language={language} onSelect={(majorGroup) => setProfileDraft((current) => ({ ...current, majorGroup }))} options={STUDENT_MAJOR_GROUPS} /></label>
              <label><strong>{t.grade}</strong><ResumeOptionGroup active={profileDraft.grade} language={language} onSelect={(grade) => setProfileDraft((current) => ({ ...current, grade }))} options={STUDENT_GRADES} /></label>
              <label><strong>{t.currentGoal}</strong><ResumeOptionGroup active={profileDraft.goal} language={language} onSelect={(goal) => setProfileDraft((current) => ({ ...current, goal }))} options={STUDENT_PROFILE_GOALS} /></label>
              <label className="resume-target-role-field"><strong>{t.targetRole}</strong><input value={profileDraft.targetRole || ''} onChange={(event) => setProfileDraft((current) => ({ ...current, targetRole: event.target.value }))} /></label>
              <button type="button" className="primary-button resume-draft-generate-button" onClick={saveResumeProfile}>{t.saveAndDraft}</button>
            </div>
          )}
          {!showProfileForm && <button type="button" className="primary-button resume-draft-generate-button" onClick={generateInitialResume}>{t.generateDraft}</button>}
          {draftResume && (
            <div className="resume-draft-result">
              <div className="resume-result-heading"><FileCheck2 size={24} /><div><h2>{t.draftResultTitle}</h2><p>{t.draftWarning}</p></div></div>
              <div className="resume-result-grid">
                {draftResume.sections.map((section) => (
                  <article className="resume-result-card" key={section.title}>
                    <h2><FileText size={18} />{section.title}</h2>
                    <ul>{section.items.map((item) => <li key={item}>{item}</li>)}</ul>
                  </article>
                ))}
              </div>
            </div>
          )}
        </section>
      )}

      {(result || selectedReport) && (
        <section className="student-result-panel resume-result-panel">
          <div className="resume-result-heading">
            <FileCheck2 size={24} />
            <div>
              <h2>{t.analysisResult}</h2>
              <p>{analysis.summary || t.analysisFallback}</p>
            </div>
          </div>
          <div className="resume-result-actions">
            <button type="button" className="secondary-button" onClick={exportPdf}><Download size={16} />{t.pdfExport}</button>
            <button type="button" className="secondary-button" onClick={exportChecklist}><Clipboard size={16} />{t.checklistExport}</button>
            <button type="button" className="secondary-button" onClick={exportDocx}><Download size={16} />{t.docxExport}</button>
          </div>
          <p className="resume-mode-tip">{t.exportNote}</p>

          <div className="resume-result-grid">
            <article className="resume-result-card resume-score-card">
              <h2><FileCheck2 size={18} />{t.totalReview}</h2>
              <strong>{analysis.score ?? '--'}</strong><span>/ 100</span>
            </article>
            <article className="resume-result-card resume-target-match-card">
              <h2><Target size={18} />{t.targetMatch}</h2>
              <strong>{targetMatchScore === null || targetMatchScore === undefined ? '--' : targetMatchScore}</strong><span>/ 100</span>
              <p>{targetMatchSummary}</p>
            </article>
            <article className="resume-result-card resume-breakdown-card">
              <h2><FileCheck2 size={18} />{t.scoreBreakdown || '分项评分'}</h2>
              <div className="resume-breakdown-grid">
                <ScoreTile label={t.scoreStructure} value={analysis.scores.structure} />
                <ScoreTile label={t.scoreProject} value={analysis.scores.project} />
                <ScoreTile label={t.scoreExpression} value={analysis.scores.expression} />
                <ScoreTile label={t.scoreJobMatch} value={analysis.scores.jobMatch} />
              </div>
            </article>
            <ListBlock title={t.strengths} items={analysis.strengths} />
            <ListBlock title={t.skillGaps} items={targetSkillGaps} />
            <ListBlock title={t.projectAdvice} items={targetProjectAdvice} />
            <article className="resume-result-card resume-project-card">
              <h2><FileText size={18} />{t.mainIssues}</h2>
              {analysis.issues.length ? analysis.issues.map((item, index) => (
                <div className="resume-project-suggestion" key={`${item.title || item}-${index}`}>
                  <strong>{item.title || String(item)}</strong>
                  {item.severity && <span className="resume-severity">{item.severity}</span>}
                  {item.description && <p>{item.description}</p>}
                </div>
              )) : <p>{t.noIssues}</p>}
            </article>
            <article className="resume-result-card resume-project-card">
              <h2><Sparkles size={18} />{t.prioritySuggestions}</h2>
              {analysis.suggestions.length ? analysis.suggestions.map((item, index) => {
                const after = item.after || item.optimized || item.text || ''
                return (
                  <div className={`resume-project-suggestion ${doneSuggestions.has(index) ? 'is-done' : ''}`} key={`${item.title || 'suggestion'}-${index}`}>
                    <strong>{item.title || `${t.suggestion} ${index + 1}`}</strong>
                    {item.before && <p>{t.originalText}: {item.before}</p>}
                    {after && <blockquote>{after}</blockquote>}
                    <div className="resume-suggestion-actions">
                      {after && <button type="button" onClick={() => copyText(after)}>{copied === after ? t.copied : t.copy}</button>}
                      {after && <button type="button" onClick={() => applySuggestion(after)}>{t.applyDraft}</button>}
                      <button type="button" onClick={() => setDoneSuggestions((current) => new Set([...current, index]))}>{t.markDone}</button>
                    </div>
                    {item.reason && <details><summary>{t.reason}</summary><p>{item.reason}</p></details>}
                  </div>
                )
              }) : <p>{t.noSuggestions}</p>}
            </article>
            <article className="resume-result-card resume-bullet-card">
              <h2><Clipboard size={18} />{t.copyReady}</h2>
              {copyReadyText.length > 0 && (
                <div className="resume-copy-list">
                  {copyReadyText.map((item) => (
                    <p key={item}>
                      {item}
                      <button type="button" onClick={() => copyText(item)}>{copied === item ? t.copied : t.copy}</button>
                    </p>
                  ))}
                </div>
              )}
              {safeText(analysis.optimizedSections.profile) && <p>{analysis.optimizedSections.profile}</p>}
              {safeList(analysis.optimizedSections.projects).map((project, index) => (
                <div className="resume-project-suggestion" key={`${project.title || 'project'}-${index}`}>
                  <strong>{project.title || t.projectExperience}</strong>
                  <ul>{safeList(project.bullets).map((item) => <li key={item}>{item}<button type="button" onClick={() => copyText(item)}>{copied === item ? t.copied : t.copy}</button></li>)}</ul>
                </div>
              ))}
              {safeList(analysis.optimizedSections.skills).length > 0 && <p>{t.skillKeywords}: {analysis.optimizedSections.skills.join(' / ')}</p>}
            </article>
            <ListBlock title={t.nextSteps} items={analysis.nextSteps} />
          </div>
          {draftSections.length > 0 && <p className="resume-mode-tip">{t.draftAdded.replace('{{count}}', draftSections.length)}</p>}
        </section>
      )}

      <section className="resume-history-panel">
        <div className="resume-history-heading">
          <div><span>{t.historyEyebrow}</span><h2>{t.history}</h2></div>
          <button type="button" className="secondary-button" onClick={() => loadHistory()}>{t.refresh}</button>
        </div>
        {comparison?.hasComparison && (
          <div className={`resume-compare-note ${comparison.delta > 0 ? 'is-up' : 'is-flat'}`}>
            {comparison.delta >= 0 ? t.compareImproved.replace('{{delta}}', comparison.delta) : t.compareDropped.replace('{{delta}}', Math.abs(comparison.delta))}
            {safeList(comparison.improvedAreas).length > 0 && <p>{t.compareBetter}: {comparison.improvedAreas.join(' / ')}</p>}
            {safeList(comparison.remainingIssues).length > 0 && <p>{t.compareRemaining}: {comparison.remainingIssues.join(' / ')}</p>}
            {safeList(comparison.nextSuggestions).length > 0 && <p>{t.compareNext}: {comparison.nextSuggestions.join(' / ')}</p>}
          </div>
        )}
        {!comparison?.hasComparison && reports.length === 1 && <p className="resume-compare-note">{t.compareHint}</p>}
        {historyError && <p className="student-form-error">{historyError}</p>}
        {reports.length === 0 ? <p className="resume-mode-tip">{t.emptyHistory}</p> : (
          <div className="resume-history-list">
            {reports.slice(0, 5).map((report) => (
              <article key={report.id}>
                <div><strong>{report.fileName || t.fileFallback}</strong><span>{formatTime(report.createdAt)}</span></div>
                <b>{reportScore(report)}</b>
                <small>{t.historyProjectScore} {reportScore(report, 'projectScore')} / {t.historyExpressionScore} {reportScore(report, 'expressionScore')}</small>
                <button type="button" onClick={() => openReport(report.id)}>{t.viewDetail}</button>
                <button type="button" onClick={() => removeReport(report.id)}><Trash2 size={14} />{t.delete}</button>
              </article>
            ))}
          </div>
        )}
      </section>

      <section className="resume-history-panel">
        <div className="resume-history-heading"><div><span>{t.githubEyebrow}</span><h2>{t.githubTitle}</h2><p>{t.githubDesc}</p></div></div>
        <div className="resume-extra-fields">
          <label>
            <span>{t.selectProject}</span>
            <select value={selectedGithubProject} onChange={(event) => setSelectedGithubProject(event.target.value)}>
              <option value="">{t.selectProject}</option>
              {githubProjects.map((project) => {
                const id = String(project.id || project.fullName || project.full_name)
                return <option key={id} value={id}>{project.fullName || project.full_name || project.name}</option>
              })}
            </select>
          </label>
        </div>
        <button type="button" className="secondary-button" disabled={!selectedGithubProject || projectLoading} onClick={generateProjectSection}>
          <Sparkles size={16} />{projectLoading ? t.generating : t.generateProject}
        </button>
        {projectSection && (
          <article className="resume-result-card resume-project-card">
            <h2>{projectSection.title || t.projectSectionFallback}</h2>
            <ul>{safeList(projectSection.bullets).map((item) => <li key={item}>{item}</li>)}</ul>
            {safeList(projectSection.techStack).length > 0 && <p>{t.techStack}: {projectSection.techStack.join(' / ')}</p>}
            {safeList(projectSection.interviewFocus).length > 0 && <p>{t.interviewFocus}: {projectSection.interviewFocus.join(' / ')}</p>}
            <button type="button" onClick={() => copyText([projectSection.title, ...safeList(projectSection.bullets)].join('\n'))}>{t.copy}</button>
          </article>
        )}
      </section>

      <section className="resume-history-panel">
        <div className="resume-history-heading"><div><span>{t.jdEyebrow}</span><h2>{t.jdTitle}</h2></div></div>
        <textarea className="resume-jd-textarea" value={jdText} onChange={(event) => setJdText(event.target.value)} placeholder={t.jdPlaceholder} />
        <div className="resume-result-actions">
          <button type="button" className="secondary-button" disabled={!jdText.trim() || jdLoading} onClick={optimizeJd}>{jdLoading ? t.optimizing : t.jdButton}</button>
          <button type="button" className="secondary-button" onClick={() => navigate('/career-assistant')}>{t.jdJump}</button>
        </div>
        {jdResult && (
          <div className="resume-result-grid">
            <ListBlock title={t.jdKeywords} items={jdResult.keywords} />
            <ListBlock title={t.missingAbilities} items={jdResult.missingAbilities} />
            <ListBlock title={t.rewriteDirections} items={jdResult.rewriteDirections} />
            <ListBlock title={t.projectAlignment} items={jdResult.projectAlignment} />
            <ListBlock title={t.interviewQuestions} items={jdResult.interviewQuestions} />
          </div>
        )}
      </section>
    </div>
  )
}

export default StudentResumePage
